import { useEffect, useRef, useState, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Tracks whether an element has scrolled into view for the first time. Falls
 * back to "shown" immediately when IntersectionObserver is unavailable or the
 * user prefers reduced motion.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.05 },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, []);

  return { ref, shown };
}

/** Fade-up class applied while revealed, kept hidden until then. */
export function revealClass(shown: boolean) {
  return shown ? "animate-fade-up" : "opacity-0";
}

/**
 * A `<section>` that fades up the first time it scrolls into view. Drop-in
 * replacement for a plain `<section>` element.
 */
export function RevealSection({
  className,
  delay,
  children,
  ...rest
}: ComponentPropsWithoutRef<"section"> & { delay?: number }) {
  const { ref, shown } = useReveal<HTMLElement>();

  return (
    <section
      ref={ref}
      className={cn(revealClass(shown), className)}
      style={shown && delay ? { animationDelay: `${delay}ms` } : undefined}
      {...rest}
    >
      {children as ReactNode}
    </section>
  );
}
